import { Flex, Text } from '@chakra-ui/react';
import { dayjs } from 'src/libs/dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { FormNumberInput } from 'src/components/molecules/formInput/FormNumberInput';
import { FormTextInput } from 'src/components/molecules/formInput/FormTextInput';
import { FormSelect } from 'src/components/molecules/formInput/FormSelect';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useFilterContext } from 'src/contexts/FilterContext';
import { useForecastingDataContext } from '../../../contexts/ForecastingDataContext';
import { Footer } from './Footer';
import { ScenarioBuilder } from './ScenarioBuilder';

type ScenarioInput = {
  scenarioName: string;
  mediaSpend: Array<{ channelId: string; spend: number }>;
};

export type ForecastFormInputs = {
  name: string;
  periods: number;
  frequency: 'weekly' | 'monthly';
  scenarios: ScenarioInput[];
};

function ForecastDateRange({
  methods,
}: {
  methods: ReturnType<typeof useForm<ForecastFormInputs>>;
}) {
  const periods = methods.watch('periods');
  const frequency = methods.watch('frequency');

  const today = dayjs();
  const start =
    frequency === 'weekly'
      ? today.startOf('week').add(1, 'week')
      : today.add(1, 'month').startOf('month');
  const end =
    frequency === 'weekly'
      ? start.add((periods || 1) - 1, 'week')
      : start.add((periods || 1) - 1, 'month');
  const totalDays = end.diff(start, 'day') + (frequency === 'weekly' ? 7 : 30);

  return (
    <Text
      fontSize="sm"
      fontFamily="marketingPage"
      color="content.muted"
      mb={{ base: '12px', md: '24px' }}>
      Forecast period: {start.format('DD MMM YYYY')} —{' '}
      {end.format('DD MMM YYYY')} ({totalDays} days)
    </Text>
  );
}

type CreateForecastProps = {
  onClose: () => void;
};

export const CreateForecast: React.FC<CreateForecastProps> = ({ onClose }) => {
  const { createForecast, baselineSpend } = useForecastingDataContext();
  const { media, currentModelVersion } = useFilterContext();
  const { toastState, showError, hideToast } = useCustomToast();

  const selectedMedia = media.filter((m) => m.selected);

  // Pre-fill "Business as usual" scenario with historical average spend
  const hasBaseline = Object.keys(baselineSpend).length > 0;

  // Normalize channel name for matching (strip all non-alphanumeric, lowercase)
  const normalizeChannel = (name: string) =>
    name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  // Build normalized lookup from baselineSpend
  const normalizedBaseline = new Map<string, number>();
  for (const [key, value] of Object.entries(baselineSpend)) {
    normalizedBaseline.set(normalizeChannel(key), value);
  }
  const getBaselineSpend = (m: { id: string; name: string }) =>
    normalizedBaseline.get(normalizeChannel(m.id)) ??
    normalizedBaseline.get(normalizeChannel(m.name)) ??
    0;

  // Check if media channels match baseline spend keys
  const hasMatchingChannels =
    hasBaseline && selectedMedia.some((m) => getBaselineSpend(m) > 0);
  const defaultScenarios: ScenarioInput[] = [
    ...(hasBaseline
      ? [
          {
            scenarioName: 'Business as usual',
            mediaSpend: selectedMedia.map((m) => ({
              channelId: m.id,
              spend: Math.round(getBaselineSpend(m)),
            })),
          },
        ]
      : []),
    {
      scenarioName: 'Scenario 1',
      mediaSpend: selectedMedia.map((m) => ({ channelId: m.id, spend: 0 })),
    },
  ];

  const methods = useForm<ForecastFormInputs>({
    defaultValues: {
      name: '',
      periods: 12,
      frequency: 'weekly',
      scenarios: defaultScenarios,
    },
  });

  if (!currentModelVersion) {
    return <ErrorNotifBox title="No model version selected" />;
  }

  if (hasBaseline && !hasMatchingChannels) {
    return (
      <ErrorNotifBox title="Forecasting is not available for this model. The model's media channel configuration is incompatible with the current setup. Please contact support or re-run the model." />
    );
  }

  const onSubmit = async (values: ForecastFormInputs) => {
    try {
      const scenarios = values.scenarios.map((s) => ({
        scenarioName: s.scenarioName,
        mediaSpend: Object.fromEntries(
          s.mediaSpend.map((ms) => [ms.channelId, ms.spend]),
        ),
      }));

      await createForecast({
        forecastName: values.name,
        periods: values.periods,
        frequency: values.frequency,
        scenarios,
      });
      onClose();
    } catch (e) {
      showError(
        'Creation failed',
        'Failed to create forecast. Please try again.',
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Flex flexDir="column">
          <FormTextInput<ForecastFormInputs>
            inputPath="name"
            placeholder="Enter forecast name"
            helperText="Used to help identify your forecast"
            label="Forecast name"
            required
            sx={{ mb: { base: '12px', md: '24px' } }}
          />
          <Flex gap={4} mb={{ base: '12px', md: '24px' }}>
            <FormNumberInput<ForecastFormInputs>
              inputPath="periods"
              placeholder="12"
              label="Number of periods"
              helperText="How many periods to forecast ahead"
              required
            />
            <FormSelect<ForecastFormInputs, string>
              inputPath="frequency"
              label="Frequency"
              options={[
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              required
            />
          </Flex>
          <ForecastDateRange methods={methods} />
          <ScenarioBuilder media={selectedMedia} />
        </Flex>
        <Footer onCancelClick={onClose} />
      </form>
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
      />
    </FormProvider>
  );
};
